import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { UserRole } from '../enums/role-name';
import { UserProfileImage } from '../models/image';
import { UserRoleStore } from '../stores/user-role.store';
import { ConsultingTeamMemberRole } from '../types/consulting-team-member';
import { Enterprise } from '../types/enterprise';
import { Facility } from '../types/facility';
import { Profile } from '../types/profile';
import { AuthService } from './../auth/auth.service';
import { AccountService } from './account.service';
import { ConsultingTeamMemberService } from './consulting-team-member.service';
import { CurrentUserStateService } from './current-user-state.service';
import { EnterpriseService } from './enterprise.service';
import { ExchangeRateService } from './exchange-rate.service';
import { FacilityService } from './facility.service';
import { MediaService } from './media.service';
import { TeamMemberService } from './team-member.service';

export type Role = UserRole | ConsultingTeamMemberRole;

@Injectable({
  providedIn: 'root',
})
export class UserRoleService {
  private enterprises: Enterprise[] = [];
  facilities: Facility[] = [];
  enterpriseFacilities: { [enterpriseId: string]: Facility[] } = {};
  enterpriseAdmin: { [enterpriseId: string]: boolean } = {};
  facilityAdmin: { [enterpriseId: string]: boolean } = {};
  facilityRoles: { [facilityId: string]: string } = {};
  enterpriseRoles: { [enterpriseId: string]: string } = {};

  accountOwnerEnterpriseId: string | undefined;
  hasEnterpriseAdmin = false;
  hasWorksiteAdmin = false;

  isLoaded = false;

  private profileSubj$: BehaviorSubject<Profile> = new BehaviorSubject<Profile>(null!);
  profile$ = this.profileSubj$.asObservable();
  profile?: Profile;

  userProfileImage?: UserProfileImage;
  userProfileImage$: BehaviorSubject<UserProfileImage> = new BehaviorSubject<UserProfileImage>(null!);

  private currentUserIdSubj$: BehaviorSubject<string> = new BehaviorSubject<string>(null!);
  currentUserId$ = this.currentUserIdSubj$.asObservable();

  private enterpriseAdminsSubj$: BehaviorSubject<{ [enterpriseId: string]: boolean }> = new BehaviorSubject<{
    [enterpriseId: string]: boolean;
  }>(null!);
  enterpriseAdmins$ = this.enterpriseAdminsSubj$.asObservable();

  constructor(
    private enterpriseService: EnterpriseService,
    private accountService: AccountService,
    private currentUser: CurrentUserStateService,
    private authService: AuthService,
    private facilityService: FacilityService,
    private teamMemberService: TeamMemberService,
    private consultingTeamMemberService: ConsultingTeamMemberService,
    private exchangeRateService: ExchangeRateService,
    private mediaService: MediaService,
    private currentUserState: CurrentUserStateService,
    private userRoleStore: UserRoleStore,
  ) {
    this.loadAll().then();
  }

  clear(): void {
    this.enterprises = [];
    this.facilities = [];
    this.enterpriseFacilities = {};
    this.enterpriseAdmin = {};
    this.facilityAdmin = {};
    this.facilityRoles = {};
    this.enterpriseRoles = {};
    this.accountOwnerEnterpriseId = undefined;
    this.hasWorksiteAdmin = false;
    this.hasEnterpriseAdmin = false;

    this.isLoaded = false;
  }

  async loadAll(useCache = true) {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    if (this.isLoaded === true) {
      return;
    }

    this.isLoaded = true;

    try {
      const [_, enterprises, profile] = await Promise.all([
        this.exchangeRateService.loadCurrencyRates()?.toPromise(),
        this.enterpriseService.loadAll(),
        this.accountService.getProfile(useCache).toPromise(),
      ]);

      this.enterprises = enterprises;
      this.setProfile(profile);

      this.mediaService.getUserProfileImageByUserId(profile.uuid!).subscribe((image) => {
        this.userProfileImage = image;
        this.userProfileImage$.next(image);
      });
    } catch (error) {
      console.error('Error loading data', error);
    }

    for (const enterprise of this.enterprises) {
      await this.facilityService.loadAll(enterprise.id).then((facilities) => {
        this.enterpriseFacilities[enterprise.id] = facilities.sort((a, b) => a.name.localeCompare(b.name));
        facilities?.forEach((f) => this.facilities.push(f));
      });

      this.teamMemberService.isEnterpriseAdminOrAccountOwner(enterprise.id).subscribe((result) => {
        this.enterpriseAdmin[enterprise.id] = result?.isEnterpriseAdminOrAccountOwner;
        this.enterpriseAdminsSubj$.next(this.enterpriseAdmin);
      });

      this.teamMemberService
        .getTeamMembersByRoles(enterprise.id, [
          UserRole.ACCOUNT_OWNER,
          UserRole.ENTERPRISE_ADMIN,
          UserRole.WORKSITE_ADMIN,
        ])
        .subscribe((users) => {
          const user = users?.find((u) => u.username === this.currentUser.username);
          if (user) {
            this.hasWorksiteAdmin = true;

            user.employments.forEach((employment) => {
              employment.facilityEmployments.forEach((facilityEmployment) => {
                const roleName = facilityEmployment.role?.name as UserRole;
                switch (roleName) {
                  case UserRole.ACCOUNT_OWNER:
                    this.accountOwnerEnterpriseId = enterprise.id;
                    this.hasEnterpriseAdmin = true;
                    this.facilityAdmin[facilityEmployment.facilityId] = true;
                    break;
                  case UserRole.ENTERPRISE_ADMIN:
                    this.hasEnterpriseAdmin = true;
                    break;
                  case UserRole.WORKSITE_ADMIN:
                    this.facilityAdmin[facilityEmployment.facilityId] = true;
                    break;
                }
              });
            });
          }
        });
    }

    await this.loadUserRole();
  }

  async loadUserRole() {
    if (!this.authService.isAuthenticated()) {
      return;
    }

    if (!this.currentUserState.userId) {
      return;
    }

    const assignments = await this.consultingTeamMemberService
      .getAssignmentsByTeamMemberId(this.currentUserState.userId)
      .toPromise();

    if (assignments && assignments.length > 0) {
      assignments.forEach((assignment) => {
        if (assignment.facilityId) {
          this.facilityRoles[assignment.facilityId] = assignment.role;
        } else if (assignment.enterpriseId) {
          this.enterpriseRoles[assignment.enterpriseId] = assignment.role;
        } else {
          // If there's no facilityId or enterpriseId, it's an executive role
          this.userRoleStore.setCurrentUserRole(assignment.role);
        }
      });

      this.userRoleStore.setFacilityRoles(this.facilityRoles);
      this.userRoleStore.setEnterpriseRoles(this.enterpriseRoles);
    }
  }

  private setProfile(profile: Profile): void {
    this.profile = profile;
    this.profileSubj$.next(profile);
    this.currentUserIdSubj$.next(profile.uuid!);
    this.currentUserState.setUserId(profile.uuid!);

    profile.employments?.forEach((employment) => {
      employment.facilityEmployments?.forEach((facilityEmployment) => {
        if (facilityEmployment.facilityId) {
          this.facilityRoles[facilityEmployment.facilityId] = facilityEmployment.role?.name!;

          if (!this.enterpriseRoles[employment.enterpriseId!]) {
            this.enterpriseRoles[employment.enterpriseId!] = facilityEmployment.role?.name!;
          }
        }
      });
    });

    this.userRoleStore.setFacilityRoles(this.facilityRoles);
    this.userRoleStore.setEnterpriseRoles(this.enterpriseRoles);
  }

  getEnterprises(): Enterprise[] {
    return this.enterprises;
  }

  getFacilities(enterpriseId: string): Facility[] {
    return this.enterpriseFacilities[enterpriseId] || [];
  }

  isEnterpriseAdmin(enterprise: Enterprise): boolean {
    return this.enterpriseAdmin[enterprise.id];
  }

  isWorksiteAdmin(facility: Facility): boolean {
    if (!facility.enterprise) {
      return false;
    }

    return this.facilityAdmin[facility.id] || this.isEnterpriseAdmin(facility.enterprise);
  }

  getRoleName(fac: Facility): string {
    return this.facilityRoles[fac.id]?.replace('_', ' ').toLowerCase();
  }

  getFirstRoleName(enterprise: Enterprise): string {
    return this.enterpriseRoles[enterprise.id]?.replace('_', ' ').toLowerCase();
  }

  getAdminEnterprises(): Enterprise[] {
    return this.enterprises.filter((e) => this.isEnterpriseAdmin(e));
  }

  getWorksiteAdminFacilities(): Facility[] {
    return this.facilities.filter((f) => this.isWorksiteAdmin(f));
  }

  getWorksiteAdminFacilitiesForEnterprise(enterprise: Enterprise): Facility[] {
    return this.facilities.filter((f) => this.isWorksiteAdmin(f) && f.enterprise?.id === enterprise.id);
  }

  isAccountOwner(): boolean {
    return !!this.accountOwnerEnterpriseId;
  }

  currentUserUUID(): string | undefined {
    return this.profile?.uuid;
  }

  getCurrentUserProfileImage(): UserProfileImage | undefined {
    return this.userProfileImage;
  }

  reloadUserProfileImage() {
    this.mediaService.getUserProfileImageByUserId(this.profile?.uuid!).subscribe((image) => {
      this.userProfileImage = image;
      this.userProfileImage$.next(image);
    });
  }

  getFacilityEmployment(facility: Facility) {
    let result = undefined;
    if (this.profile && facility) {
      const employment = this.profile.employments?.find((e) => e.enterpriseId === facility.enterprise?.id);
      if (employment) {
        result = employment.facilityEmployments.find(
          (facilityEmployment) => facilityEmployment.facilityId === facility.id,
        );
      }
    }

    return result;
  }
}
